function escapeHtml(value) {
  return String(value || '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]))
}

export function normalizeWikiText(value) {
  return String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ').trim()
}

export function wikiAnchor(value) {
  return normalizeWikiText(value).replace(/\s+/g, '-') || 'seccion'
}

function safeLink(url) {
  const value = String(url || '').trim()
  if (/^(https?:\/\/|\/|#)/i.test(value)) return value
  return ''
}

function safeImage(url, backendurl) {
  const value = String(url || '').trim()
  const asset = value.match(/^wiki-asset:\/\/([a-f0-9]{24})$/i)
  if (asset) return `${backendurl || '/'}api/v1/wiki/assets/${asset[1]}/content`
  if (/^\/wiki\/manual-v1\/images\/[a-zA-Z0-9._-]+$/.test(value)) return value
  return ''
}

function decorateText(value) {
  return escapeHtml(value)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
}

function plainMarkdownText(value) {
  return String(value || '')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[`*_~>|#]+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function renderInline(value, backendurl) {
  const tokenRE = /!\[([^\]]*)\]\(([^)]+)\)|\[([^\]]+)\]\(([^)]+)\)/g
  let result = '', cursor = 0, match
  while ((match = tokenRE.exec(value)) !== null) {
    result += decorateText(value.slice(cursor, match.index))
    if (match[1] !== undefined) {
      const src = safeImage(match[2], backendurl)
      result += src
        ? `<img src="${escapeHtml(src)}" alt="${escapeHtml(match[1])}" loading="lazy" tabindex="0">`
        : escapeHtml(match[1])
    } else {
      const href = safeLink(match[4])
      if (!href) result += escapeHtml(match[3])
      else {
        const external = /^https?:\/\//i.test(href)
        result += `<a href="${escapeHtml(href)}"${external ? ' target="_blank" rel="noopener noreferrer"' : ''}>${escapeHtml(match[3])}</a>`
      }
    }
    cursor = tokenRE.lastIndex
  }
  return result + decorateText(value.slice(cursor))
}

function isTableDivider(line) {
  return /^\s*\|?(\s*:?-+:?\s*\|)+\s*$/.test(line)
}

function tableCells(line) {
  return line.trim().replace(/^\||\|$/g, '').split('|').map(cell => cell.trim())
}

export function renderWikiMarkdown(markdown, backendurl) {
  const lines = String(markdown || '').replace(/\r\n/g, '\n').split('\n')
  const html = [], toc = [], counts = {}
  let i = 0
  while (i < lines.length) {
    const line = lines[i].trim()
    if (!line) { i++; continue }
    const heading = line.match(/^(#{1,6})\s+(.+)$/)
    if (heading) {
      const level = heading[1].length
      const rawLabel = heading[2]
      const label = plainMarkdownText(rawLabel)
      const base = wikiAnchor(label)
      counts[base] = (counts[base] || 0) + 1
      const anchor = counts[base] > 1 ? `${base}-${counts[base]}` : base
      toc.push({ level, label, anchor })
      html.push(`<h${level} id="${anchor}">${renderInline(rawLabel, backendurl)}</h${level}>`)
      i++; continue
    }
    if (line.startsWith('|') && i + 1 < lines.length && isTableDivider(lines[i + 1])) {
      const headers = tableCells(line); i += 2
      const rows = []
      while (i < lines.length && lines[i].trim().startsWith('|')) { rows.push(tableCells(lines[i])); i++ }
      html.push('<div class="wiki-table-wrap"><table><thead><tr>' + headers.map(c => `<th>${renderInline(c, backendurl)}</th>`).join('') + '</tr></thead><tbody>' +
        rows.map(row => '<tr>' + row.map(c => `<td>${renderInline(c, backendurl)}</td>`).join('') + '</tr>').join('') + '</tbody></table></div>')
      continue
    }
    if (/^[-*]\s+/.test(line)) {
      const items = []
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) { items.push(lines[i].trim().replace(/^[-*]\s+/, '')); i++ }
      html.push('<ul>' + items.map(item => `<li>${renderInline(item, backendurl)}</li>`).join('') + '</ul>'); continue
    }
    if (/^\d+\.\s+/.test(line)) {
      const items = []
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) { items.push(lines[i].trim().replace(/^\d+\.\s+/, '')); i++ }
      html.push('<ol>' + items.map(item => `<li>${renderInline(item, backendurl)}</li>`).join('') + '</ol>'); continue
    }
    if (/^!\[[^\]]*\]\([^)]+\)$/.test(line)) {
      html.push(`<figure>${renderInline(line, backendurl)}</figure>`); i++; continue
    }
    const paragraph = [line]; i++
    while (i < lines.length && lines[i].trim() && !/^(#{1,6})\s+|^[-*]\s+|^\d+\.\s+|^\|/.test(lines[i].trim())) {
      paragraph.push(lines[i].trim()); i++
    }
    html.push(`<p>${renderInline(paragraph.join(' '), backendurl)}</p>`)
  }
  return { html: html.join('\n'), toc }
}
